import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAccessToken } from "@/hooks";
import { ClassesApi } from "@/openapi/generated";
import type { UpdateClassRequest } from "@/openapi/generated/models";
import { QueryKeys } from "@/lib/types";
import { createAuthenticatedApi, getApiErrorMessage } from "@/lib/utils";
import type { useToast } from "@/components/feedback";

export const useClassUpdate = (
  toast: ReturnType<typeof useToast>,
) => {
  const accessToken = useAccessToken();
  const queryClient = useQueryClient();

  const { mutate, isPending, isSuccess, isError } = useMutation({
    mutationFn: async (updateClassRequest: UpdateClassRequest) => {
      if (!accessToken) {
        throw new Error("Authentication token is required");
      }

      const service = createAuthenticatedApi(ClassesApi, accessToken);

      return service.apiClassesPut({
        updateClassRequest,
      });
    },
    onSuccess: async () => {
      // Invalidate both public and admin classes queries, and individual class queries
      await queryClient.invalidateQueries({
        predicate: (query) => {
          return (
            Array.isArray(query.queryKey) &&
            (query.queryKey[0] === QueryKeys.Classes ||
              query.queryKey[0] === QueryKeys.Class)
          );
        },
      });
      toast({
        title: "Class updated",
        description: "Class updated successfully!",
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    },
    onError: async (error: any) => {
      const errorMessage = await getApiErrorMessage(error, "Failed to update class");
      
      toast({
        title: "Error",
        description: errorMessage,
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
    },
  });

  return { mutate, isPending, isError, isSuccess };
};
